// The agent hub's runs and agents, from GET /agent-hub/*. Mirror of sbly-dash-collector
// src/intelligence/agentHub/types.ts (the hub itself is shareably/agent-hub).
export type RunStatus = 'running' | 'done' | 'stopped_at_budget' | 'rate_limited' | 'error' | 'skipped';
export type EventType = 'assistant' | 'tool_call' | 'tool_result' | 'live_action' | 'denied' | 'pr_opened' | 'note' | 'rate_limit' | 'error' | 'report' | 'consult' | 'hand_off' | 'billing_guard';
export type Command = 'pause' | 'resume' | 'run_now';

export interface AgentState {
  name: string;
  summary: string; // one plain line (agent.yaml)
  group?: string | null; // its section on the Agents page; none = listed on its own
  description: string; // the skill's long description
  schedule: string;
  priority: number;
  minutesToday: number;
  paused: boolean;
  running: string | null;
  nextRunAt: string | null;
  liveActions: string[];
  status: 'draft' | 'active' | 'retired';
  onDemand: boolean; // takes tasks (page, API, other agents)
  canRunNow?: boolean; // has a job of its own (a schedule), so Run now means something
  expertise: string | null; // set when other agents may consult it
  canAsk: string[];
  queuedTasks: number;
  settings?: AgentSettings; // missing until the hub runs a version that reports it
  lastRun?: RunBrief | null;
  stats?: AgentStats;
  failing?: boolean; // its last two runs both failed
  floor?: number | null; // which floor of the Office tab it works on, once the hub sends it (office/layout.ts floorOf)
  scheduleRaw?: string | null; // its schedule as the hub runs it (null: not scheduled)
  run?: RunSource | null; // where its run settings come from; missing until the hub runs a version that reads them from this page
}

export const RUN_FIELDS = ['status', 'schedule', 'onDemand', 'priority', 'paused'] as const;
export type RunField = typeof RUN_FIELDS[number];
/** agent-hub agentSettings.ts RunSource: agent.yaml's run settings, which of them this page overrides and who last changed them, and any the hub couldn't use. */
export interface RunSource {
  yaml: { status: AgentState['status']; schedule?: string | null; onDemand: boolean; priority: number };
  fromPage: RunField[];
  by: string | null;
  at: string | null;
  problems: string[];
}
/** Run settings saved on this page; a field not here is agent.yaml's. schedule "none": not scheduled. */
export interface SavedRunSettings { status?: 'active' | 'draft'; schedule?: string; onDemand?: boolean; priority?: number; paused?: boolean; by?: string; at?: string }

/** What the agent's agent.yaml sets; it changes only through a PR to the hub repo. */
export interface AgentSettings { workdir: string; mcp: string[]; skills: string[] }

/** An agent's effort, today (Pacific) and over 7 days, from the hub. Agents have no limits yet; this is what they'd be set from. */
export interface AgentStats {
  runsToday: number; minutesToday: number; runs7d: number; minutes7d: number;
  avgMinutes: number | null; avgSteps: number | null; avgOutputTokens: number | null; prs7d: number; minutesPerPr: number | null; errors7d: number;
}

/** The hub's brief of an agent's newest run (the full record: GET /agent-hub/runs/:id). */
export interface RunBrief {
  runId: string; agent: string; status: RunRow['status']; startedAt: string; minutes: number | null; turns: number | null;
  outputTokens: number | null; subject: string | null; summary: string | null; error: string | null;
}

export interface HubState {
  host: string; // engineers only: the page shows every hub as one
  at: string;
  version?: string | null;
  runs?: number;
  maxConcurrent: number;
  pausedUntil: string | null;
  billingGuard: string | null; // a session reported billing other than the Claude subscription: every agent is stopped
  safetyStop?: string | null; // a safety hook changed outside a hub update: no agent starts
  pauseReason: string | null;
  plan?: HubPlan | null; // the subscription's windows and the estimated weekly limit (agent-hub planLimit.ts)
  queued: string[];
  agents: AgentState[];
}

/** A run row from the API (Snowflake column names, lower-cased by the driver). */
export interface RunRow {
  run_id: string;
  agent: string;
  host: string | null;
  triggered_by: string | null;
  status: RunStatus;
  started_at: string;
  finished_at: string | null;
  minutes: number | null;
  turns: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  model: string | null;
  summary: string | null;
  report?: string | null;
  pr_urls: string[] | string | null;
  live_actions?: unknown;
  live_action_count?: number | null;
  denials?: unknown;
  denial_count?: number | null;
  error: string | null;
  mode?: 'run' | 'consult' | null;
  task?: string | null;
  agent_version?: string | null;
  parent_run_id?: string | null;
}

export interface EventRow { seq: number; ts: string; type: EventType; tool: string | null; summary: string; detail: unknown }

/** GET /agent-hub/agents: the hub's live state (from Firebase; nothing here reads Snowflake). */
export interface AgentsResponse {
  hub: { state: HubState; minutesSince: number } | null;
}

// The hub rewrites its live state at least every minute; past this it is down (the Mac is off, asleep or the service stopped).
export const HUB_DOWN_MINUTES = 10;

/** One hub on its own (GET /agent-hub/agents/hosts): for engineers, in the folded Hubs section. */
export interface HubHost {
  host: string;
  at: string;
  minutesSince: number;
  version: string | null;
  runs: number | null;
  maxConcurrent: number;
  running: string[];
  pausedUntil: string | null;
  billingGuard: string | null;
  safetyStop: string | null;
  machine?: MachineLoad | null; // the hub's machine, sampled at most once a minute (agent-hub machine.ts)
  slots?: { previewRunning: number; preview: number; other: number } | null;
}

export interface MachineLoad {
  at: string;
  cpu: { load1: number; load5: number; cores: number; pct: number };
  memory: { totalGb: number; freePct: number | null }; // freePct: macOS memory_pressure's system-wide free %
  swapUsedGb: number | null;
  diskFreeGb: number | null;
}

// Snowflake VARIANT columns come back as JSON strings or values depending on the driver path.
export const parseVariant = <T,>(v: unknown, fallback: T): T => (v == null ? fallback : typeof v === 'string' ? (JSON.parse(v) as T) : (v as T));

export const STATUS_LOOK: Record<RunStatus, { label: string; fg: string; bg: string }> = {
  running: { label: 'Running', fg: '#1E40AF', bg: '#DBEAFE' },
  done: { label: 'Done', fg: '#065F46', bg: '#D1FAE5' },
  stopped_at_budget: { label: 'Hit time budget', fg: '#92400E', bg: '#FEF3C7' },
  rate_limited: { label: 'Usage limit', fg: '#9A3412', bg: '#FFEDD5' },
  error: { label: 'Error', fg: '#991B1B', bg: '#FEE2E2' },
  skipped: { label: 'Nothing to do', fg: '#475569', bg: '#F1F5F9' },
};

/** A PR in the agent hub's PR pipeline (GET /agent-hub/prs). */
export interface PipelinePrRow {
  subject: string;
  repo: string;
  pr_number: number;
  title: string | null;
  author: string | null;
  url: string | null;
  head_sha: string | null;
  stage: string;
  verdict: string | null;
  gate: unknown;
  stages: unknown;
  merged_sha: string | null;
  updated_at: string;
}

/** An approval (GET /agent-hub/approvals). Decided only in Slack, by the approvers in policy.yaml. */
export interface ApprovalRow {
  approval_id: string;
  kind: 'live_action' | 'merge' | 'revert' | 'proposal'; // proposal: a manager agent's (agent-hub manager.ts)
  agent: string;
  subject: string | null;
  title: string;
  evidence: string | null;
  impact: string | null;
  status: string;
  requested_at: string;
  expires_at: string;
  decided_by: string | null;
  decided_at: string | null;
  slack: unknown;
}

/** A person an agent's Slack alerts @-mention (GET/PUT /agent-hub/agents/:name/settings). */
export interface AlertRecipient {
  slackId: string;
  name: string;
}

/** One of the hub's areas (Meta, Google, creative, pipelines …) and who owns it: any agent @-mentions them on what they need to act on. */
export interface Area {
  id: string;
  name: string;
  people: AlertRecipient[];
  updatedBy?: string;
  updatedAt?: string;
}

/** An agent's settings saved on this page (they override its agent.yaml; the hub applies them live). */
export interface SavedAgentSettings {
  alertRecipients?: AlertRecipient[];
  run?: SavedRunSettings;
  updatedBy?: string;
  updatedAt?: string;
}

/** GET /agent-hub/usage: what each agent consumed (collector src/intelligence/agentHub/usage.ts). tokens: weighted by price; cost: API-equivalent (tooltips). */
export interface UsageTotals { runs: number; tokens: number; avgTokens: number; cost: number; avgCost: number; inputTokens: number; outputTokens: number }
export interface UsageRunRef { runId: string; startedAt: string; tokens: number; cost: number; minutes: number | null; turns: number | null; subject: string | null; status: string }
export interface AgentUsage { agent: string; today: UsageTotals; d7: UsageTotals; d30: UsageTotals; prev7: UsageTotals; trendPct: number | null; cacheReadShare: number | null; topRuns: UsageRunRef[] }
export interface UsageReport {
  at: string;
  totals: { today: UsageTotals; d7: UsageTotals; d30: UsageTotals };
  agents: AgentUsage[];
  prs: Array<{ subject: string; repo: string; tokens: number; cost: number; runs: number; byAgent: Record<string, number> }>; // byAgent: tokens
  repos: Array<{ repo: string; prs: number; tokens: number; perPr: number; cost: number; costPerPr: number }>;
  hints: string[];
}

/** GET /agent-hub/run-scores/summary: how a manager agent scored each agent's runs (collector agentHub/scores.ts). */
export interface RubricQuality {
  rubric: string;
  avg: number | null;
  runs: number;
  weekly: Array<{ weekStart: string; avg: number | null; runs: number }>;
  worst: Array<{ runId: string; score: number; reason: string; subject: string | null; startedAt: string | null }>;
}
export interface QualityReport {
  at: string;
  days?: number; // the period the report covers (missing from a collector without ?days)
  agents: Array<{ agent: string; vertical: string; rubrics: RubricQuality[] }>;
  proposals: Array<{ id: string; agent: string; title: string; status: string; decidedAt: string | null }>;
}

/** The Claude subscription's windows at the latest reading, and its weekly limit in weighted tokens, estimated by the hub. */
export interface PlanWindow { utilization: number; resetsAt: string | null }
export interface HubPlan {
  windows: { at: string; fiveHour: PlanWindow | null; sevenDay: PlanWindow | null } | null;
  // From snapshots every 6 h: the hub's tokens over the weekly window's rise. Kevin's own use counts too, so a worst case.
  estimate: { weeklyLimit: number; low: number; high: number; pairs: number; at: string } | null;
}
